// CASH1-S2-U1 (docs/build-briefs/CASH1-S2-U1-pay-screens.md; PRD P1.1; DEC-399): the pure rules behind the Cash choice at Pay.
// No DOM and no session import, so a plain node:test file proves them directly (test/cash1-s2-ui-tender-model.test.ts).
//
// Money is never typed. What a cash payment is sent is Exact (the amount due), one of the quick-cash amounts, or a counted
// breakdown of bills and coins. The server derives the due and the change again and refuses anything else, so these figures
// only choose what to offer. There is no tip on cash (owner ruling 2026-10-13, later).

import { breakdownOf, totalCents, type DrawerCounts } from "./cashDrawerModel.ts";

/** Mirrors CASH_TENDER.quickCashStepsCents in src/config/cashTender.ts. The ui bundle cannot import from src, so a test fails
 * when the two lists differ. */
export const QUICK_CASH_STEPS_CENTS: readonly number[] = [500, 1000, 2000, 5000, 10000];

/** Mirrors CASH_TENDER.maxQuickCashButtons. */
export const MAX_QUICK_CASH_BUTTONS = 4;

/** The quick-cash amounts for an amount due: the next $5, $10, $20, $50 and $100 above it, de-duplicated, ascending, at most
 * four. Never the amount due itself (Exact covers that). Mirrors quickCashAmountsCents in src/domain/cashTender.ts. */
export function quickCashAmounts(dueCents: number): number[] {
  if (!Number.isSafeInteger(dueCents) || dueCents <= 0) return [];
  const amounts = new Set<number>();
  for (const step of QUICK_CASH_STEPS_CENTS) {
    const next = Math.ceil(dueCents / step) * step;
    if (next > dueCents) amounts.add(next);
  }
  return [...amounts].sort((a, b) => a - b).slice(0, MAX_QUICK_CASH_BUTTONS);
}

/** Change for a tendered amount, or null while it is below the due (the button stays off). */
export function changeFor(dueCents: number, tenderedCents: number): number | null {
  if (!Number.isSafeInteger(dueCents) || !Number.isSafeInteger(tenderedCents)) return null;
  if (dueCents <= 0 || tenderedCents < dueCents) return null;
  return tenderedCents - dueCents;
}

export type CashChoiceKind = "exact" | "quick" | "other";

export interface CashChoice {
  kind: CashChoiceKind;
  /** The quick amount, for kind "quick". */
  cents?: number;
}

/** What the tender is for a choice: the cents handed over and the breakdown sent (Other only). */
export function tenderFor(
  dueCents: number,
  choice: CashChoice,
  counts: DrawerCounts,
): { tenderedCents: number; denominations?: Record<string, number> } | null {
  if (choice.kind === "exact") return dueCents > 0 ? { tenderedCents: dueCents } : null;
  if (choice.kind === "quick") {
    const cents = choice.cents;
    if (cents === undefined || !quickCashAmounts(dueCents).includes(cents)) return null;
    return { tenderedCents: cents };
  }
  const counted = totalCents(counts);
  if (changeFor(dueCents, counted) === null) return null;
  return { tenderedCents: counted, denominations: breakdownOf(counts) };
}

// ---------------------------------------------------------------------
// The Card or Cash choice at Pay
// ---------------------------------------------------------------------

export type PayTender = "card" | "cash";

/** Whether Pay offers Cash, from the `GET /cash-drawer/current` body. Only the boolean true turns it on; a missing field, a
 * string or a failed read is off. */
export function cashOffered(body: unknown): boolean {
  if (typeof body !== "object" || body === null) return false;
  return (body as { cashEnabled?: unknown }).cashEnabled === true;
}

/** CASH1-S2-U1-F1. Cash is on but cannot be taken here: no drawer is open and this person cannot open one. A short label, never
 * a sentence. Null when Cash can be used (a drawer is open, or this person can open one and the open flow runs first). */
export function cashLockedLabel(input: { drawerOpen: boolean; canOpen: boolean }): string | null {
  return input.drawerOpen || input.canOpen ? null : "Drawer closed";
}

/** A tender is chosen. With Cash not offered the sheet is the card sheet it always was, so a choice is implied. With it offered,
 * neither is preselected. */
export function tenderChosen(offered: boolean, tender: PayTender | undefined): boolean {
  return !offered || tender !== undefined;
}

/** Confirm is enabled: a tender is chosen, a tip is chosen (Cash carries an explicit no-tip, never a tip), and the pay warning
 * is known safe. */
export function confirmEnabled(input: {
  offered: boolean;
  tender: PayTender | undefined;
  tipChosen: boolean;
  warningKnownSafe: boolean;
  /** CARDDEFAULT2. The saved-card list outcome is in, so the card the sheet charges is settled. Absent counts as settled. */
  cardsResolved?: boolean;
  /** PAYFIX1. The tip and total on the sheet were read for the tip now chosen. Absent counts as synced. */
  tipSynced?: boolean;
}): boolean {
  return (
    tenderChosen(input.offered, input.tender) &&
    input.tipChosen &&
    input.tipSynced !== false &&
    input.warningKnownSafe &&
    input.cardsResolved !== false
  );
}

/** The sheet's tip section shows unless Cash is picked. */
export function tipSectionVisible(tender: PayTender | undefined): boolean {
  return tender !== "cash";
}

export function dollarsOf(cents: number): string {
  const magnitude = Math.abs(cents);
  return `$${Math.trunc(magnitude / 100)}.${String(magnitude % 100).padStart(2, "0")}`;
}

/** The footer button of the Cash screen: the amount handed over. */
export function takeCashLabel(dueCents: number, tenderedCents: number): string {
  return changeFor(dueCents, tenderedCents) === null
    ? "Take cash"
    : `Take ${dollarsOf(tenderedCents)}`;
}

/** The change shown after the payment: the server's figure. */
export function changeHeadline(changeCents: number): string {
  return changeCents > 0 ? dollarsOf(changeCents) : "No change due";
}

/** The change a payment attempt records, read off the server's row (a bigint column arrives as a string). Null when the
 * attempt is not a cash payment or the figure is unreadable, so nothing is drawn on a guess. */
export function changeFromAttempt(attempt: {
  tender?: unknown;
  change_cents?: unknown;
  cash_tendered_cents?: unknown;
}): { changeCents: number; tenderedCents: number } | null {
  if (attempt.tender !== "cash") return null;
  const change = Number(attempt.change_cents);
  const tendered = Number(attempt.cash_tendered_cents);
  if (!Number.isSafeInteger(change) || !Number.isSafeInteger(tendered)) return null;
  if (change < 0 || tendered <= 0) return null;
  return { changeCents: change, tenderedCents: tendered };
}
